import 'dayjs/locale/de';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { Region } from './region';
import { DEFAULT_REGION } from './region';

dayjs.extend(utc);
dayjs.extend(timezone);

export function getCurrentDateUnix() {
  return Math.floor(Date.now() / 1000);
}

export function formatReleaseDateShort(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('ddd DD MMM');
}

export function formatReleaseDate(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('ddd DD MMM YY');
}

export function formatReservationTimer(reservationExpiryTimestamp: number) {
  return dayjs(reservationExpiryTimestamp).format('mm:ss').toUpperCase();
}

export function formatReleaseDateLong(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('ddd DD MMM YY, h:mm A');
}

export function formatRaffleDate(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('D MMM ha');
}

export const formatReleaseDateLarge = (
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) => {
  const ts = unixTimestamp * 1000;

  return (
    dayjs(ts).tz(region.timeZone).locale(region.lang).format('ddd DD MMM') +
    ' at ' +
    dayjs(ts)
      .tz(region.timeZone)
      .locale(region.lang)
      .format('hh:mm a')
      .toUpperCase()
  );
};

const HOUR_MS = 1000 * 60 * 60;

export function formatArticleCardDate(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION,
  now: number = Date.now()
) {
  const date = dayjs(unixTimestamp * 1000).tz(region.timeZone);
  const today = dayjs(now).tz(region.timeZone);

  if (date.isSame(today, 'day')) {
    const hours = Math.floor((now - unixTimestamp * 1000) / HOUR_MS);

    if (hours < 1) return 'Just now';

    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  }

  if (date.isSame(today.subtract(1, 'day'), 'day')) return 'Yesterday';

  return date.locale(region.lang).format('DD MMM YY');
}

export function formatFeatureDate(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('DD MMM YYYY');
}
